import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckCircle2Icon, ClockIcon, ReceiptTextIcon, ShieldCheckIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Field';
import { currency, hoursLeft, longDate } from '../../utils/format';
import { GROCERY_LABEL } from '../../utils/pricing';
import { useLocale } from '../../contexts/LocaleContext';
import { useAppData } from '../../contexts/AppDataContext';
import { spring } from '../../utils/motion';
import { cn } from '../../utils/cn';
import type { Quote } from '../../types/booking';
const statusTone = {
  pending: 'accent',
  accepted: 'success',
  declined: 'danger',
  expired: 'neutral',
  superseded: 'neutral'
} as const;
const statusLabel = {
  pending: 'Awaiting your answer',
  accepted: 'Accepted',
  declined: 'Declined',
  expired: 'Expired',
  superseded: 'Replaced by a newer version'
};
export function QuoteCard({
  quote,
  role
}: {
  quote: Quote;
  role: 'chef' | 'customer';
}) {
  const {
    locale,
    currencyCode,
    t
  } = useLocale();
  const {
    acceptQuote,
    requestChanges
  } = useAppData();
  const reduce = useReducedMotion();
  const [stamped, setStamped] = useState(false);
  const [asking, setAsking] = useState(false);
  const [changeNote, setChangeNote] = useState('');
  const live = quote.status === 'pending';
  const expiresIn = hoursLeft(quote.expiresAt);
  const accept = () => {
    setStamped(true);
    acceptQuote(quote.id);
  };
  return <motion.article initial={reduce ? {
    opacity: 0
  } : {
    opacity: 0,
    y: 12,
    scale: 0.98
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} transition={reduce ? {
    duration: 0.16
  } : {
    type: 'spring',
    stiffness: 320,
    damping: 30
  }} className={cn('relative overflow-hidden rounded-xl border bg-surface shadow-lift', live ? 'border-accent' : 'border-hairline')} data-id="element-211" data-mp="1lg8en2cyznoa">
      <AnimatePresence data-id="element-212" data-mp="246pva8kj2sta">
        {stamped && !reduce && <motion.span aria-hidden="true" initial={{
        x: '-110%'
      }} animate={{
        x: '110%'
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.28,
        ease: [0.23, 1, 0.32, 1]
      }} onAnimationComplete={() => setStamped(false)} className="pointer-events-none absolute inset-y-0 z-10 w-1/2 bg-accent/25" data-id="element-213" data-mp="2bdbss05d2i32" />}
      </AnimatePresence>

      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-hairline px-4 py-3" data-id="element-214" data-mp="2v1sy836xvvea">
        <div data-id="element-215" data-mp="2z366ejxi8aod">
          <p className="flex items-center gap-2 font-display text-base font-semibold text-ink" data-id="element-216" data-mp="340o39by8yzg0">
            <ReceiptTextIcon className="h-4 w-4 text-action" aria-hidden="true" data-id="element-217" data-mp="38zhlfhohbemi" />
            {t('quote.title')} · v{quote.version}
          </p>
          <p className="mt-0.5 text-[13px] text-ink-2" data-id="element-218" data-mp="19nosbw4tatf2">
            {quote.menuName} · {quote.guests} guests · {longDate(quote.date, locale)} at {quote.time}
          </p>
        </div>
        <div className="text-right" data-id="element-219" data-mp="1730li8z15qhm">
          <Badge tone={statusTone[quote.status]} data-id="element-220" data-mp="1741xqgrs8j0x">{statusLabel[quote.status]}</Badge>
          {live && <p className="mt-1.5 flex items-center justify-end gap-1 text-[12px] text-ink-3" data-id="element-221" data-mp="1295vyowquham">
              <ClockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-222" data-mp="xa1mdx5wqhjr" />
              {t('quote.expires')} {expiresIn} hrs
            </p>}
        </div>
      </header>

      <div className="px-4 py-3" data-id="element-223" data-mp="1q0b7wszkcpfn">
        <table className="w-full text-sm" data-id="element-224" data-mp="1pygx47i0adpa">
          <caption className="sr-only" data-id="element-225" data-mp="1l2rv7xiikkpt">Quote breakdown</caption>
          <tbody data-id="element-226" data-mp="1g2nblymb79vd">
            {quote.lines.map(line => <tr key={line.id} className="align-top" data-id="element-227" data-mp="1g12k6hvak71q">
                <th scope="row" className="py-1.5 pr-3 text-left font-normal" data-id="element-228" data-mp="1b42bs0ayys7g">
                  <span className="block font-medium text-ink" data-id="element-229" data-mp="4ngumvlis3fo">{line.label}</span>
                  {line.detail && <span className="block text-[12px] text-ink-3" data-id="element-230" data-mp="3vtrpg9zxs7yk">{line.detail}</span>}
                </th>
                <td className="tnum py-1.5 text-right font-medium text-ink" data-id="element-231" data-mp="1oius6tgloftm">
                  {line.amount === 0 ? <span className="text-ink-3" data-id="element-232" data-mp="1t91egmnn3o8p">at cost</span> : currency(line.amount, locale, currencyCode)}
                </td>
              </tr>)}
            <tr data-id="element-233" data-mp="1em86k2gg9kqj">
              <th scope="row" className="border-t border-hairline py-1.5 pr-3 text-left font-normal text-ink-2" data-id="element-234" data-mp="1eikbuzz9qsa8">
                Service fee (10%)
              </th>
              <td className="tnum border-t border-hairline py-1.5 text-right text-ink" data-id="element-235" data-mp="279lwvhpn7i1o">
                {currency(quote.serviceFee, locale, currencyCode)}
              </td>
            </tr>
            <tr data-id="element-236" data-mp="1xa3eh5pds4bq">
              <th scope="row" className="py-1.5 pr-3 text-left font-normal text-ink-2" data-id="element-237" data-mp="1xamxyquoj0rv">
                VAT (7%)
              </th>
              <td className="tnum py-1.5 text-right text-ink" data-id="element-238" data-mp="lvpuibxr090z">{currency(quote.tax, locale, currencyCode)}</td>
            </tr>
          </tbody>
          <tfoot data-id="element-239" data-mp="qvvohyrhq7m8">
            <tr data-id="element-240" data-mp="qwdv9pbs448e">
              <th scope="row" className="border-t border-hairline pt-2.5 text-left font-display text-base font-semibold text-ink" data-id="element-241" data-mp="qsq24ra8xju7">
                Total
              </th>
              <td className="tnum border-t border-hairline pt-2.5 text-right font-display text-lg font-semibold text-ink" data-id="element-242" data-mp="302phstlatoj2">
                {currency(quote.total, locale, currencyCode)}
              </td>
            </tr>
          </tfoot>
        </table>

        <dl className="mt-3 grid grid-cols-2 gap-3 rounded-lg bg-oat p-3 text-[13px]" data-id="element-243" data-mp="2mbuedwtkjzmv">
          <div data-id="element-244" data-mp="2hczmm5dmj0qm">
            <dt className="text-ink-2" data-id="element-245" data-mp="2hdj4xn76rr56">{t('quote.deposit')}</dt>
            <dd className="tnum font-semibold text-ink" data-id="element-246" data-mp="2hawnmvuhtx9r">
              {currency(quote.depositDue, locale, currencyCode)}{' '}
              <span className="font-normal text-ink-3" data-id="element-247" data-mp="2qdxqmno2b897">({quote.depositPercent}%)</span>
            </dd>
          </div>
          <div data-id="element-248" data-mp="22nbkx31j602d">
            <dt className="text-ink-2" data-id="element-249" data-mp="22msdig9iau2l">{t('quote.balance')}</dt>
            <dd className="tnum font-semibold text-ink" data-id="element-250" data-mp="1ymrl1rpkoicj">{currency(quote.balanceDue, locale, currencyCode)}</dd>
          </div>
        </dl>

        <p className="mt-3 text-[13px] text-ink-2" data-id="element-251" data-mp="1l40507sz207">
          <span className="font-medium text-ink" data-id="element-252" data-mp="6koyp9j298ce">{GROCERY_LABEL[quote.groceries]}.</span> {quote.note}
        </p>

        {quote.allergiesAcknowledged && <p className="mt-3 flex items-center gap-2 text-[13px] text-primary" data-id="element-253" data-mp="bc6xbqxm2wu7">
            <ShieldCheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-254" data-mp="g9glz4d6f4gz" />
            Allergy record acknowledged by the chef
          </p>}
      </div>

      {role === 'customer' && live && <footer className="border-t border-hairline px-4 py-3" data-id="element-255" data-mp="3js9w176qxb6e">
          {asking ? <div data-id="element-256" data-mp="3opju1b87av5u">
              <Textarea rows={3} value={changeNote} onChange={e => setChangeNote(e.target.value)} placeholder="Could we do four hours instead, and skip the wine?" aria-label="What would you like changed?" data-id="element-257" data-mp="3op0axr6te0q8" />
              <div className="mt-2 flex gap-2" data-id="element-258" data-mp="18xw07oohhir9">
                <Button size="sm" variant="primary" disabled={changeNote.trim().length < 4} onClick={() => {
            requestChanges(quote.id, changeNote);
            setChangeNote('');
            setAsking(false);
          }} data-id="element-259" data-mp="2th83sccvx7nc">
                  Send to chef
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setAsking(false)} data-id="element-260" data-mp="29u0g1nxuqpwl">
                  Cancel
                </Button>
              </div>
            </div> : <div className="flex flex-wrap items-center gap-2" data-id="element-261" data-mp="20ske84l3rw3z">
              <Button variant="action" size="md" onClick={accept} data-id="element-262" data-mp="20t2h3k29t8kv">
                {t('quote.accept')} · {currency(quote.depositDue, locale, currencyCode)}
              </Button>
              <Button variant="outline" size="md" onClick={() => setAsking(true)} data-id="element-263" data-mp="3v5ijuavuy4kz">
                {t('quote.counter')}
              </Button>
            </div>}
        </footer>}

      {quote.status === 'accepted' && <footer className="flex items-center gap-2 border-t border-hairline bg-success-soft px-4 py-2.5 text-[13px] text-success" data-id="element-264" data-mp="2z4qyjat28xpe">
          <CheckCircle2Icon className="h-4 w-4" aria-hidden="true" data-id="element-265" data-mp="2pdd2xe48ci1j" />
          Accepted — deposit of {currency(quote.depositDue, locale, currencyCode)} authorized.
        </footer>}
    </motion.article>;
}